import {
  blankStaff, blankWeek, carryOverMinutes, cellWon, emptyMinutes, generalIsAuto, INCOME_COL_LABEL, INCOME_COLS, MINUTES_ROWS_MAX, MINUTES_STAFF, MINUTES_TEXT_MAX,
  MINUTES_WEEKS, minutesPeriod, minutesTitle, minutesTotals, monthWeeks, nextId, pruneOverrides, signedWon, STAFF_AREAS, TOTAL_SESSION_AREAS, weekLabel,
  type MeetingMinutes, type MinutesChange, type MinutesPerfRow, type MinutesStaff, type StaffArea,
} from '@selfon/domain/browser';
import { useState, type ReactNode } from 'react';
import type { StaffRow } from '../api';
import { Alert, errorText } from '../components/ui';
import { fullDateTime, won } from '../format';

/*
 * 결산 회의록(센터 엑셀 양식). 규칙은 docs/CLOSING_MINUTES_RULES.md.
 * 모든 칸을 고칠 수 있다. 자동 계산 칸을 덮어쓰면 색으로 표시하고 ↺ 로 되돌린다.
 */

const nf = (v: number) => v.toLocaleString('ko-KR');
const pct = (v: number) => `${v.toFixed(2)}%`;
const monthTitle = (m: string) => `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;
const move = <T,>(xs: T[], i: number, d: number) => {
  const j = i + d;
  if (j >= 0 && j < xs.length) [xs[i], xs[j]] = [xs[j]!, xs[i]!];
};

function MoneyInput({ value, onChange, label, signed }: { value: number; onChange: (v: number) => void; label: string; signed?: boolean }) {
  return <input className="acct-money" type="number" inputMode={signed ? 'text' : 'numeric'} min={signed ? undefined : 0} step={1} placeholder="0" aria-label={label}
    value={value || ''} onChange={(e) => onChange(signed ? signedWon(e.target.value) : cellWon(e.target.value))} />;
}

/** 건수: 해당 건이 없으면 빈칸이 아니라 0 을 보여 준다 */
function CountInput({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return <input className="acct-money minutes-count" type="number" inputMode="numeric" min={0} step={1} aria-label={label} value={value}
    onFocus={(e) => e.target.select()} onChange={(e) => onChange(cellWon(e.target.value))} />;
}

/** 자동 계산 칸: 비워 두거나 ↺ 를 누르면 계산값으로 돌아간다. 입력 중에는 글자를 그대로 두고 칸을 벗어날 때 반영한다. */
function CalcCell({ value, auto, manual, label, format, onChange }: {
  value: number; auto: number; manual: boolean; label: string; format: (v: number) => string; onChange: (v: number | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const text = draft.replace(/[,\s원%]/g, '');
    setDraft(null);
    if (!text) onChange(null);
    else if (Number.isFinite(Number(text)) && Number(text) !== value) onChange(Number(text));
  };
  return (
    <span className={`minutes-calc${manual ? ' manual' : ''}`}>
      <input type="text" inputMode="text" aria-label={manual ? `${label} (수동 입력, 자동 계산 ${format(auto)})` : label} title={manual ? `자동 계산: ${format(auto)}` : undefined}
        value={draft ?? format(value)} onFocus={(e) => { setDraft(String(value)); e.target.select(); }} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }} />
      {manual && <button type="button" className="minutes-reset" title="자동 계산으로 되돌리기" aria-label={`${label} 자동 계산으로 되돌리기`} onClick={() => onChange(null)}>↺</button>}
    </span>
  );
}

/** 행·열·주차의 순서 변경과 삭제 */
function Tools({ label, onMove, onRemove, sideways }: { label: string; onMove: (d: number) => void; onRemove?: () => void; sideways?: boolean }) {
  return (
    <span className="minutes-tools">
      <button type="button" aria-label={`${label} ${sideways ? '왼쪽' : '위'}로`} onClick={() => onMove(-1)}>{sideways ? '←' : '↑'}</button>
      <button type="button" aria-label={`${label} ${sideways ? '오른쪽' : '아래'}로`} onClick={() => onMove(1)}>{sideways ? '→' : '↓'}</button>
      {onRemove && <button type="button" aria-label={`${label} 삭제`} onClick={onRemove}>✕</button>}
    </span>
  );
}

function Sec({ n, title, children, note }: { n: number; title: string; children: ReactNode; note?: ReactNode }) {
  return (
    <section className="minutes-sec" aria-labelledby={`min-sec-${n}`}>
      <h3 id={`min-sec-${n}`}><span className="minutes-no">{n}</span>{title}</h3>
      {children}
      {note && <p className="hint">{note}</p>}
    </section>
  );
}

export function MinutesForm({ month, initial, history, staff, centerName, loadPrev, onSave }: {
  month: string; initial: MeetingMinutes | null; history: MinutesChange[]; staff: StaffRow[]; centerName: string;
  loadPrev: () => Promise<MeetingMinutes | null>; onSave: (m: MeetingMinutes) => Promise<void>;
}) {
  const [m, setM] = useState<MeetingMinutes>(() => initial ?? emptyMinutes(month, centerName));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editWeeks, setEditWeeks] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'info'; text: string } | null>(null);
  const t = minutesTotals(m);
  const up = (f: (x: MeetingMinutes) => MeetingMinutes | void) => { setM((x) => { const c = structuredClone(x); return f(c) ?? c; }); setDirty(true); setMsg(null); };
  const calc = (key: string, value: number, label: string, format: (v: number) => string = nf) => (
    <CalcCell key={key} value={value} auto={t.auto[key] ?? 0} manual={key in m.overrides} label={label} format={format}
      onChange={(v) => up((x) => { if (v === null) delete x.overrides[key]; else x.overrides[key] = v; })} />
  );
  const sureRemove = (name: string, hasData: boolean) => !hasData || window.confirm(`${name} 항목에 입력한 값이 있습니다. 삭제할까요?`);

  const addStaff = () => up((x) => {
    const s = blankStaff(nextId('s', x.staff.map((v) => v.id)), `담당자 ${x.staff.length + 1}`);
    x.staff.push(s);
    for (const r of x.perfRows) r.counts[s.id] = 0;
  });
  const removeStaff = (s: MinutesStaff) => {
    if (!sureRemove(s.code, t.staffFinal[s.id] !== 0)) return;
    up((x) => {
      x.staff = x.staff.filter((v) => v.id !== s.id);
      for (const r of x.perfRows) delete r.counts[s.id];
      return pruneOverrides(x);
    });
  };
  const addRow = (group: MinutesPerfRow['group']) => up((x) => {
    const row: MinutesPerfRow = { key: nextId('R', x.perfRows.map((r) => r.key)), label: '', group, counts: Object.fromEntries(x.staff.map((s) => [s.id, 0])) };
    const last = x.perfRows.map((r) => r.group).lastIndexOf(group);
    x.perfRows.splice(last + 1, 0, row);
  });
  const removeRow = (r: MinutesPerfRow) => {
    if (!sureRemove(r.label || '빈', t.perfRowTotal[r.key] !== 0)) return;
    up((x) => { x.perfRows = x.perfRows.filter((v) => v.key !== r.key); return pruneOverrides(x); });
  };
  const moveRow = (r: MinutesPerfRow, d: number) => up((x) => {
    // 같은 묶음 안에서만 움직인다
    const same = x.perfRows.flatMap((v, i) => (v.group === r.group ? [i] : []));
    const at = same.findIndex((i) => x.perfRows[i]!.key === r.key);
    const a = same[at]!, b = same[at + d];
    if (b !== undefined) [x.perfRows[a], x.perfRows[b]] = [x.perfRows[b]!, x.perfRows[a]!];
  });
  const addWeek = () => {
    setEditWeeks(true);
    up((x) => {
      const last = x.weeks[x.weeks.length - 1]?.end ?? `${month}-01`;
      x.weeks.push(blankWeek(nextId('w', x.weeks.map((w) => w.id)), last, last));
    });
  };
  const resetWeeks = () => {
    const auto = monthWeeks(month);
    if (m.weeks.slice(auto.length).some((w) => t.weekIncome[w.id] || w.expense || w.note)) {
      setMsg({ kind: 'error', text: `뒤쪽 주차에 입력한 값이 있어 ${auto.length}주로 맞출 수 없습니다. 먼저 옮기거나 삭제해 주세요.` });
      return;
    }
    up((x) => {
      const kept = x.weeks.slice(0, auto.length);
      auto.forEach((w, i) => { kept[i] = kept[i] ? { ...kept[i]!, ...w } : blankWeek(nextId('w', [...x.weeks, ...kept].map((v) => v.id)), w.start, w.end); });
      x.weeks = kept;
      return pruneOverrides(x);
    });
  };
  const copyPrev = async () => {
    setBusy(true); setMsg(null);
    try {
      const prev = await loadPrev();
      if (!prev) { setMsg({ kind: 'info', text: '전월에 저장한 회의록이 없습니다.' }); return; }
      if ((dirty || initial) && !window.confirm('지금 화면의 내용을 지우고 전월 명단·지출로 다시 채울까요? 저장해야 반영됩니다.')) return;
      setM(carryOverMinutes(prev, month)); setDirty(true);
      setMsg({ kind: 'info', text: '전월의 치료사 명단과 지출을 이어받았습니다. 수입·건수는 0 으로 비웠습니다.' });
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await onSave(m); setDirty(false); setMsg({ kind: 'ok', text: `${monthTitle(month)} 결산 회의록을 저장했습니다.` }); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(false); }
  };

  const perfRows = (group: MinutesPerfRow['group']) => m.perfRows.filter((r) => r.group === group).map((r) => (
    <tr key={r.key}>
      <th scope="row">
        <span className="minutes-rowhead">
          <input className="minutes-label" value={r.label} maxLength={30} placeholder="항목명" aria-label={`${r.label || '새'} 항목명`} onChange={(e) => up((x) => { x.perfRows.find((v) => v.key === r.key)!.label = e.target.value; })} />
          <Tools label={r.label || '새 항목'} onMove={(d) => moveRow(r, d)} onRemove={() => removeRow(r)} />
        </span>
      </th>
      {m.staff.map((s) => (
        <td key={s.id} className="num">
          {r.key === 'GENERAL' && generalIsAuto(s)
            ? calc(`general:${s.id}`, t.general[s.id]!, `${s.code} 일반수업`)
            : <CountInput value={r.counts[s.id] ?? 0} onChange={(v) => up((x) => { x.perfRows.find((w) => w.key === r.key)!.counts[s.id] = v; })} label={`${s.code} ${r.label}`} />}
        </td>
      ))}
      <td className="num minutes-total">{calc(`rowTotal:${r.key}`, t.perfRowTotal[r.key]!, `${r.label} 합계`)}</td>
    </tr>
  ));
  const negGeneral = m.staff.filter((s) => (t.general[s.id] ?? 0) < 0).map((s) => s.code);
  const who = (id: string) => staff.find((s) => s.userId === id)?.name ?? '원장님';

  return (
    <form className="minutes" onSubmit={(e) => { e.preventDefault(); void save(); }} aria-labelledby="minutes-title">
      <div className="minutes-head">
        <label className="minutes-center"><span className="sr-only">센터명</span>
          <input value={m.centerName} maxLength={60} placeholder="센터명" onChange={(e) => up((x) => { x.centerName = e.target.value; })} />
        </label>
        <h2 id="minutes-title" className="sr-only">{monthTitle(month)} 결산 회의록</h2>
        <label className="minutes-title"><span className="sr-only">제목</span>
          <input value={m.title || minutesTitle(m.centerName, month)} maxLength={100} onChange={(e) => up((x) => { x.title = e.target.value === minutesTitle(x.centerName, month) ? '' : e.target.value; })} />
        </label>
        <div className="minutes-meta">
          <label>결산 기간 <input value={m.period || minutesPeriod(month)} maxLength={40} onChange={(e) => up((x) => { x.period = e.target.value === minutesPeriod(month) ? '' : e.target.value; })} /></label>
          <label>회의일 <input type="date" max="9999-12-31" value={m.meetingDate} onChange={(e) => up((x) => { x.meetingDate = e.target.value; })} /></label>
          <label>참석자 <input value={m.attendees} maxLength={200} placeholder="예: 원장, 실장, 담당 선생님" onChange={(e) => up((x) => { x.attendees = e.target.value; })} /></label>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => void copyPrev()} disabled={busy}>전월 복사</button>
      </div>

      <div className="minutes-summary" role="group" aria-label="요약">
        <div><span>총수입</span><strong>{won(t.totalIncome)}</strong></div>
        <div><span>총지출</span><strong>{won(t.expenseTotal)}</strong></div>
        <div className={t.cashNet < 0 ? 'neg' : ''}><span>총수입 − 총지출</span><strong>{won(t.cashNet)}</strong></div>
        <div className={t.netProfit < 0 ? 'neg' : ''}><span>순이익</span><strong>{won(t.netProfit)}</strong></div>
      </div>
      <p className="hint"><span className="minutes-legend" aria-hidden="true" /> 색칠된 칸은 자동 계산을 직접 고친 값입니다. ↺ 를 누르면 자동 계산으로 돌아갑니다.</p>

      <Sec n={1} title="주간 수입" note="주차는 일~토 기준입니다. 치료비 환불은 현금에서 빼서 적습니다(음수 가능). 바우처 입금은 계좌이체에 넣지 않습니다. 발달재활은 세무상 수입금액에서 제외됩니다.">
        <div className="minutes-staff-tools">
          <button type="button" className="btn btn-outline btn-sm" onClick={addWeek} disabled={m.weeks.length >= MINUTES_WEEKS}>+ 주차</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditWeeks((v) => !v)} aria-pressed={editWeeks}>기간 수정</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetWeeks}>달력대로 맞추기</button>
        </div>
        <div className="table-wrap minutes-wrap" tabIndex={0} role="region" aria-label="주간 수입 표 (가로로 스크롤)">
          <table className="table minutes-table">
            <caption className="sr-only">주간 수입</caption>
            <thead><tr><th scope="col">주차</th><th scope="col">기간</th>{INCOME_COLS.map((c) => <th key={c} scope="col" className="num">{INCOME_COL_LABEL[c]}</th>)}<th scope="col">비고</th><th scope="col" className="num">주 합계</th></tr></thead>
            <tbody>
              {m.weeks.map((w, i) => (
                <tr key={w.id}>
                  <th scope="row">
                    <span className="minutes-rowhead">{i + 1}주
                      <Tools label={`${i + 1}주`} onMove={(d) => up((x) => { move(x.weeks, i, d); })} onRemove={m.weeks.length > 1 ? () => {
                        if (sureRemove(`${i + 1}주`, Boolean(t.weekIncome[w.id] || w.expense || w.note))) up((x) => { x.weeks.splice(i, 1); return pruneOverrides(x); });
                      } : undefined} />
                    </span>
                  </th>
                  <td className="nowrap small">
                    {editWeeks ? (
                      <span className="minutes-dates">
                        <input type="date" max="9999-12-31" value={w.start} aria-label={`${i + 1}주 시작일`} onChange={(e) => { const v = e.target.value; if (v) up((x) => { x.weeks[i]!.start = v; }); }} />
                        <input type="date" max="9999-12-31" value={w.end} aria-label={`${i + 1}주 종료일`} onChange={(e) => { const v = e.target.value; if (v) up((x) => { x.weeks[i]!.end = v; }); }} />
                      </span>
                    ) : weekLabel(w.start, w.end)}
                  </td>
                  {INCOME_COLS.map((c) => <td key={c} className="num"><MoneyInput value={w.amounts[c]} signed={c === 'CASH'} label={`${i + 1}주 ${INCOME_COL_LABEL[c]}`} onChange={(v) => up((x) => { x.weeks[i]!.amounts[c] = v; })} /></td>)}
                  <td><input className="minutes-note" value={w.note} maxLength={100} aria-label={`${i + 1}주 비고`} onChange={(e) => up((x) => { x.weeks[i]!.note = e.target.value; })} /></td>
                  <td className="num minutes-total">{calc(`weekTotal:${w.id}`, t.weekIncome[w.id]!, `${i + 1}주 합계`)}</td>
                </tr>
              ))}
              <tr className="minutes-sum"><th scope="row" colSpan={2}>수입 합계</th>{INCOME_COLS.map((c) => <td key={c} className="num">{calc(`incomeCol:${c}`, t.incomeByCol[c], `${INCOME_COL_LABEL[c]} 합계`)}</td>)}<td /><td className="num">{calc('totalIncome', t.totalIncome, '월 총수입')}</td></tr>
            </tbody>
          </table>
        </div>
      </Sec>

      <div className="minutes-two minutes-two-wide">
        <Sec n={2} title="주간 지출">
          <div className="table-wrap minutes-wrap">
            <table className="table minutes-table">
              <caption className="sr-only">주간 지출</caption>
              <thead><tr><th scope="col">주차</th><th scope="col" className="num">금액</th></tr></thead>
              <tbody>
                {m.weeks.map((w, i) => (
                  <tr key={w.id}>
                    <th scope="row">{i + 1}주</th>
                    <td className="num"><MoneyInput value={w.expense} label={`${i + 1}주 지출`} onChange={(v) => up((x) => { x.weeks[i]!.expense = v; })} /></td>
                  </tr>
                ))}
                <tr className="minutes-sum"><th scope="row">지출 합계</th><td className="num">{calc('weeklyExpenseTotal', t.weeklyExpenseTotal, '주간 지출 합계')}</td></tr>
                <tr className="minutes-sum"><th scope="row">총수입 − 총지출</th><td className="num">{calc('cashNet', t.cashNet, '총수입 − 총지출')}</td></tr>
              </tbody>
            </table>
          </div>
        </Sec>

        <Sec n={4} title="지출내역" note="체크로 계산에 넣을 항목을 정합니다. 주요 = (생활비, 예비비 외) 지출에 포함, 적립 = 순이익에 다시 더함, 별관 = 별관외지출에서 뺌.">
          <div className="table-wrap minutes-wrap">
            <table className="table minutes-table minutes-expense">
              <caption className="sr-only">항목별 지출내역</caption>
              <thead><tr><th scope="col">항목</th><th scope="col" className="num">금액</th><th scope="col" className="num">비율</th><th scope="col">주요</th><th scope="col">적립</th><th scope="col">별관</th></tr></thead>
              <tbody>
                {m.expenses.map((e, i) => (
                  <tr key={e.key}>
                    <th scope="row">
                      <span className="minutes-rowhead">
                        <input className="minutes-label" value={e.label} maxLength={30} placeholder="항목명" aria-label={`${e.label || '빈'} 항목명`} onChange={(v) => up((x) => { x.expenses[i]!.label = v.target.value; })} />
                        <Tools label={e.label || '빈 항목'} onMove={(d) => up((x) => { move(x.expenses, i, d); })} onRemove={() => {
                          if (sureRemove(e.label || '빈', e.amount !== 0)) up((x) => { x.expenses.splice(i, 1); return pruneOverrides(x); });
                        }} />
                      </span>
                    </th>
                    <td className="num"><MoneyInput value={e.amount} label={`${e.label || '빈 항목'} 금액`} onChange={(v) => up((x) => { x.expenses[i]!.amount = v; })} /></td>
                    <td className="num">{calc(`ratio:${e.key}`, t.expenseRatio[e.key]!, `${e.label || '빈 항목'} 비율`, pct)}</td>
                    {(['ops', 'reserve', 'annex'] as const).map((f) => (
                      <td key={f} className="minutes-flag"><input type="checkbox" checked={e[f]} aria-label={`${e.label || '빈 항목'} ${{ ops: '주요 지출', reserve: '적립', annex: '별관' }[f]}`} onChange={(v) => up((x) => { x.expenses[i]![f] = v.target.checked; })} /></td>
                    ))}
                  </tr>
                ))}
                <tr><td colSpan={6}><button type="button" className="btn btn-outline btn-sm" disabled={m.expenses.length >= MINUTES_ROWS_MAX} onClick={() => up((x) => { x.expenses.push({ key: nextId('E', x.expenses.map((v) => v.key)), label: '', amount: 0, ops: false, reserve: false, annex: false }); })}>+ 지출 항목</button></td></tr>
                <tr className="minutes-sum"><th scope="row">총지출</th><td className="num">{calc('expenseTotal', t.expenseTotal, '총지출')}</td><td colSpan={4} /></tr>
                <tr className="minutes-sum"><th scope="row">(생활비, 예비비 외) 지출</th><td className="num">{calc('opsExpense', t.opsExpense, '(생활비, 예비비 외) 지출')}</td><td className="num">{calc('opsRatio', t.opsRatio, '(생활비, 예비비 외) 지출 비율', pct)}</td><td colSpan={3} /></tr>
                <tr className="minutes-sum"><th scope="row">별관외지출</th><td className="num">{calc('baseExpense', t.baseExpense, '별관외지출')}</td><td colSpan={4} /></tr>
                <tr className="minutes-sum minutes-final"><th scope="row">순이익</th><td className="num">{calc('netProfit', t.netProfit, '순이익')}</td><td colSpan={4} /></tr>
              </tbody>
            </table>
          </div>
          <p className={`minutes-check${t.expenseGap ? ' bad' : ''}`} role="status">
            {t.expenseGap === 0 ? '주간 지출과 항목별 지출 합계가 일치합니다.' : `대조 필요 · 주간 지출 − 항목별 지출 = ${won(t.expenseGap)}`}
          </p>
        </Sec>
      </div>

      <Sec n={3} title="치료사별 수업 건수" note="건이 없는 칸은 0 으로 둡니다. 언어·놀이·감통은 전체 수업수를 넣으면 일반수업 = 전체 수업수 − 특수교육 − 발달재활 로 계산합니다. 비워 두면 일반수업을 직접 입력합니다. 노쇼·취소도 건수에 포함되므로 청구 회기수와 구분해 검토하세요.">
        <div className="minutes-staff-tools">
          <button type="button" className="btn btn-outline btn-sm" onClick={addStaff} disabled={m.staff.length >= MINUTES_STAFF.max}>+ 담당자</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => addRow('EVAL')} disabled={m.perfRows.length >= MINUTES_ROWS_MAX}>+ 평가·상담 항목</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => addRow('CLASS')} disabled={m.perfRows.length >= MINUTES_ROWS_MAX}>+ 수업 항목</button>
          <span className="muted small">{m.staff.length}명 / 최대 {MINUTES_STAFF.max}명</span>
        </div>
        <div className="table-wrap minutes-wrap" tabIndex={0} role="region" aria-label="치료사별 수업 건수 표 (가로로 스크롤)">
          <table className="table minutes-table minutes-perf">
            <caption className="sr-only">치료사별 수업 건수</caption>
            <thead>
              <tr>
                <th scope="col">항목</th>
                {m.staff.map((s, i) => (
                  <th key={s.id} scope="col" className="minutes-staff">
                    <input value={s.code} maxLength={20} aria-label={`담당자 ${i + 1} 코드`} onChange={(e) => up((x) => { x.staff[i]!.code = e.target.value; })} />
                    <input value={s.name} maxLength={20} placeholder="이름" aria-label={`${s.code} 이름`} onChange={(e) => up((x) => { x.staff[i]!.name = e.target.value; })} />
                    <select value={s.area} aria-label={`${s.code} 영역`} onChange={(e) => up((x) => { x.staff[i]!.area = e.target.value as StaffArea | ''; return pruneOverrides(x); })}>
                      <option value="">영역</option>
                      {STAFF_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <Tools sideways label={s.code} onMove={(d) => up((x) => { move(x.staff, i, d); })} onRemove={m.staff.length > MINUTES_STAFF.min ? () => removeStaff(s) : undefined} />
                  </th>
                ))}
                <th scope="col" className="num">total</th>
              </tr>
            </thead>
            <tbody>
              {perfRows('EVAL')}
              <tr className="minutes-sum"><th scope="row">평가·상담 소계</th>{m.staff.map((s) => <td key={s.id} className="num">{calc(`evalSub:${s.id}`, t.evalSubtotal[s.id]!, `${s.code} 평가·상담 소계`)}</td>)}<td className="num">{calc('evalTotal', t.evalTotal, '평가·상담 소계 합계')}</td></tr>
              <tr>
                <th scope="row">전체 수업수</th>
                {m.staff.map((s, i) => (
                  <td key={s.id} className="num">
                    {(TOTAL_SESSION_AREAS as readonly string[]).includes(s.area)
                      ? <input className="acct-money" type="number" inputMode="numeric" min={0} step={1} aria-label={`${s.code} 전체 수업수`} value={s.totalSessions ?? ''}
                          onChange={(e) => up((x) => { x.staff[i]!.totalSessions = e.target.value === '' ? null : cellWon(e.target.value); return pruneOverrides(x); })} />
                      : <span className="muted">–</span>}
                  </td>
                ))}
                <td />
              </tr>
              {perfRows('CLASS')}
              <tr className="minutes-sum"><th scope="row">수업 소계</th>{m.staff.map((s) => <td key={s.id} className="num">{calc(`classSub:${s.id}`, t.classSubtotal[s.id]!, `${s.code} 수업 소계`)}</td>)}<td className="num">{calc('classTotal', t.classTotal, '수업 소계 합계')}</td></tr>
              <tr>
                <th scope="row">add guarantee</th>
                {m.staff.map((s, i) => <td key={s.id} className="num"><CountInput value={s.addGuarantee} label={`${s.code} add guarantee`} onChange={(v) => up((x) => { x.staff[i]!.addGuarantee = v; })} /></td>)}
                <td className="num minutes-total">{calc('guaranteeTotal', t.guaranteeTotal, 'add guarantee 합계')}</td>
              </tr>
              <tr className="minutes-sum minutes-final"><th scope="row">Final</th>{m.staff.map((s) => <td key={s.id} className="num">{calc(`final:${s.id}`, t.staffFinal[s.id]!, `${s.code} Final`)}</td>)}<td className="num">{calc('finalTotal', t.finalTotal, 'Final 합계')}</td></tr>
            </tbody>
          </table>
        </div>
        {negGeneral.length > 0 && <p className="minutes-check bad" role="status">확인 필요 · {negGeneral.join(', ')} 일반수업이 0 보다 작습니다. 전체 수업수를 확인해 주세요.</p>}
      </Sec>

      <div className="minutes-two">
        <Sec n={5} title="상의할 내용 및 필요물품">
          <textarea rows={7} maxLength={MINUTES_TEXT_MAX} aria-label="상의할 내용 및 필요물품" value={m.agenda} onChange={(e) => up((x) => { x.agenda = e.target.value; })}
            placeholder={'논의사항:\n결정 내용:\n담당자:\n완료 예정일:\n필요물품:'} />
        </Sec>
        <Sec n={6} title="타기관 의뢰 치료비 및 청구 내용" note="아동 이름 대신 내부 참조번호를 적어 주세요.">
          <textarea rows={7} maxLength={MINUTES_TEXT_MAX} aria-label="타기관 의뢰 치료비 및 청구 내용" value={m.referral} onChange={(e) => up((x) => { x.referral = e.target.value; })}
            placeholder={'의뢰 기관:\n내부 참조번호:\n청구 내용:\n확인 사항:'} />
        </Sec>
      </div>

      <details className="minutes-details">
        <summary>계산식 보기</summary>
        <ul className="plain small">
          <li>주 합계 = 그 주 모든 결제수단의 합, 월 총수입 = 주 합계의 합</li>
          <li>Final = 수업 소계 + add guarantee + 평가·상담 소계</li>
          <li>일반수업(전체 수업수 입력 시) = 전체 수업수 − 특수교육 − 발달재활</li>
          <li>총지출 = 모든 지출 항목의 합, 총수입 − 총지출 = 월 총수입 − 주간 지출 합계</li>
          <li>(생활비, 예비비 외) 지출 = '주요'에 체크한 항목의 합, 비율 = 이 금액 ÷ 총지출</li>
          <li>별관외지출 = 총지출 − '별관'에 체크한 항목, 항목별 비율 = 항목 ÷ 별관외지출(생활비만 ÷ 총지출)</li>
          <li>순이익 = 월 총수입 − 별관외지출 + '적립'에 체크한 항목</li>
        </ul>
      </details>
      <details className="minutes-details">
        <summary>변경 이력 ({history.length}건)</summary>
        {history.length === 0 ? <p className="muted small">아직 변경 이력이 없습니다.</p> : (
          <ul className="plain small minutes-history">
            {[...history].reverse().slice(0, 100).map((c, i) => <li key={i}><span className="muted">{fullDateTime(c.at)} · {who(c.by)}</span> {c.label}: {c.from || '(빈 값)'} → {c.to || '(빈 값)'}</li>)}
          </ul>
        )}
      </details>

      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <div className="minutes-actions">
        <span className="muted small" role="status">{dirty ? '저장하지 않은 변경이 있습니다.' : initial ? '저장된 회의록입니다.' : '아직 저장하지 않았습니다.'}</span>
        <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>{busy ? '저장 중…' : '회의록 저장'}</button>
      </div>
    </form>
  );
}
